import { useRef, useState } from "react"
import { Download, Upload, Trash2, FileText, Link2, ExternalLink } from "lucide-react"
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { updateTechnicalFilePatch, deleteTechnicalFile } from "@/modules/Events/service/EventsService"
import { EventoDataType } from "@/types/events"
import { useLoading } from "@/hooks/useLoading"
import { toast } from "sonner"

interface FichaTecnicaPanelProps {
  evento: EventoDataType
  canManage: boolean
  onUpdate: (url: string | undefined) => void
}

// Uploads são enviados para o S3 pelo backend; qualquer outra URL é um link externo
const isUploadedFile = (url: string) => {
  if (url.startsWith("blob:")) return true
  try {
    return new URL(url).hostname.endsWith(".amazonaws.com")
  } catch {
    return false
  }
}

const isViewablePdf = (url: string) => {
  if (url.startsWith("blob:")) return true
  try {
    return new URL(url).pathname.toLowerCase().endsWith(".pdf")
  } catch {
    return false
  }
}

export function FichaTecnicaPanel({ evento, canManage, onUpdate }: FichaTecnicaPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showLoading, hideLoading } = useLoading()
  const [linkEditing, setLinkEditing] = useState(false)
  const [linkValue, setLinkValue] = useState("")
  const [linkSaving, setLinkSaving] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append("id", String(evento.id))
    formData.append("origem", evento.origem)
    formData.append("file", file)
    try {
      await updateTechnicalFilePatch(formData)
      toast.success("Ficha técnica atualizada com sucesso!")
      setLinkEditing(false)
      onUpdate(URL.createObjectURL(file))
    } catch {
      toast.error("Erro ao fazer upload da ficha técnica.")
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const handleSaveLink = async () => {
    const url = linkValue.trim()
    if (!/^https?:\/\/.+/.test(url)) {
      toast.error("Informe um link válido (http:// ou https://).")
      return
    }
    setLinkSaving(true)
    const formData = new FormData()
    formData.append("id", String(evento.id))
    formData.append("origem", evento.origem)
    formData.append("url", url)
    try {
      await updateTechnicalFilePatch(formData)
      toast.success("Link da ficha técnica salvo com sucesso!")
      setLinkEditing(false)
      setLinkValue("")
      onUpdate(url)
    } catch {
      toast.error("Erro ao salvar link da ficha técnica.")
    } finally {
      setLinkSaving(false)
    }
  }

  const handleDelete = async () => {
    showLoading()
    try {
      await deleteTechnicalFile(evento.id, evento.origem)
      toast.success("Ficha técnica removida com sucesso!")
      onUpdate(undefined)
    } catch {
      toast.error("Erro ao remover ficha técnica.")
    } finally {
      hideLoading()
    }
  }

  const handleDownload = () => {
    const a = document.createElement("a")
    a.href = evento.url_ficha_tecnica!
    a.download = ""
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const startLinkEdit = () => {
    setLinkValue(evento.url_ficha_tecnica ?? "")
    setLinkEditing(true)
  }

  const linkForm = (
    <div className="flex w-full gap-2">
      <Input
        className="flex-1"
        placeholder="https://..."
        value={linkValue}
        onChange={(e) => setLinkValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSaveLink()}
      />
      <Button size="sm" disabled={linkSaving} onClick={handleSaveLink}>
        {linkSaving ? "Salvando..." : "Salvar"}
      </Button>
      <Button size="sm" variant="outline" disabled={linkSaving} onClick={() => setLinkEditing(false)}>
        Cancelar
      </Button>
    </div>
  )

  return (
    <>
      {evento.url_ficha_tecnica ? (
        <>
          <div className="flex flex-wrap gap-2">
            {isUploadedFile(evento.url_ficha_tecnica) ? (
              <>
                <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={handleDownload}>
                  <Download className="h-4 w-4" />
                  Download
                </Button>
                {canManage && (
                  <>
                    <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4" />
                      Substituir
                    </Button>
                    <Button variant="destructive" size="sm" className="flex-1 gap-2" onClick={handleDelete}>
                      <Trash2 className="h-4 w-4" />
                      Deletar
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={handleDownload}>
                  <ExternalLink className="h-4 w-4" />
                  Ver mais
                </Button>
                {canManage && (
                  <>
                    <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={startLinkEdit}>
                      <Link2 className="h-4 w-4" />
                      Editar
                    </Button>
                    <Button variant="destructive" size="sm" className="flex-1 gap-2" onClick={handleDelete}>
                      <Trash2 className="h-4 w-4" />
                      Deletar
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
          {canManage && linkEditing && linkForm}
          {isViewablePdf(evento.url_ficha_tecnica) ? (
            <div className="h-[600px] overflow-hidden rounded-lg border border-border">
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer fileUrl={evento.url_ficha_tecnica} theme="dark" defaultScale={SpecialZoomLevel.PageWidth} />
              </Worker>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-border p-4">
              <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <a
                href={evento.url_ficha_tecnica}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate text-sm text-foreground underline underline-offset-2"
              >
                {evento.url_ficha_tecnica}
              </a>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <FileText className="h-12 w-12 text-muted" />
          <p className="text-sm text-muted-foreground">Nenhuma ficha técnica cadastrada.</p>
          {canManage && !linkEditing && (
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4" />
                Fazer upload
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={startLinkEdit}>
                <Link2 className="h-4 w-4" />
                Inserir link
              </Button>
            </div>
          )}
          {canManage && linkEditing && linkForm}
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.doc,.docx"
        onChange={handleFileChange}
      />
    </>
  )
}
